import { useDisclosure } from '@chakra-ui/react'
import { UseQueryResult } from '@tanstack/react-query'
import moment from 'moment'
import { createContext, useContext, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { MedicalRecordStatus } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/MedicalRecordStatus'
import { ResponsibleMedicalRecordSummaryList } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordSummary'
import { useListResponsibleMedicalRecordQuery } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/presentation/hooks/useListResponsibleMedicalRecordQuery'
import { PersonaInfoSummary } from '@/modules/shared/schemas/PersonaInfoSummary'

import { MedicalRecordReviewType } from '../constants/MedicalRecordReviewType'
import MedicalRecordManagementRevision from '../views/MedicalRecordManagementTable/MedicalRecordManagementRevision'

type ContextType = {
  filter: {
    month: string
    personaName: string | undefined
    status: MedicalRecordStatus
  }
  rawInputs: {
    personaName: string | undefined
  }
  setMonth: React.Dispatch<React.SetStateAction<string>>
  setPersonaName: React.Dispatch<React.SetStateAction<string | undefined>>
  setStatus: React.Dispatch<React.SetStateAction<MedicalRecordStatus>>
  listResponsibleMedicalRecordQuery:
    | UseQueryResult<ResponsibleMedicalRecordSummaryList>
    | undefined
  isReviewViewOpen: boolean
  onReviewViewOpen: () => void
  onReviewViewClose: () => void
  selectedItemId: string
  setSelectedItemId: React.Dispatch<React.SetStateAction<string>>
  patient: PersonaInfoSummary
  setPatient: React.Dispatch<React.SetStateAction<PersonaInfoSummary>>
  reviewMode: MedicalRecordReviewType
  setReviewMode: React.Dispatch<React.SetStateAction<MedicalRecordReviewType>>
}

const initialValue = {
  filter: {
    month: '',
    personaName: undefined,
    status: 'WAITING_FOR_REVIEW' as const
  },
  rawInputs: {
    personaName: undefined
  },
  setMonth: () => {},
  setPersonaName: () => {},
  setStatus: () => {},
  listResponsibleMedicalRecordQuery: undefined,
  isReviewViewOpen: false,
  onReviewViewOpen: () => {},
  onReviewViewClose: () => {},
  selectedItemId: '',
  setSelectedItemId: () => {},
  patient: {
    avatarUrl: '',
    userId: '',
    username: ''
  },
  setPatient: () => {},
  reviewMode: MedicalRecordReviewType.enum.REVIEW,
  setReviewMode: () => {}
} satisfies ContextType

const medicalRecordManagementContext = createContext<ContextType>(initialValue)

export const useMedicalRecordManagementContext = () =>
  useContext(medicalRecordManagementContext)

export default function MedicalRecordManagementContextProvider({
  children
}: {
  children: React.ReactNode
}) {
  const [month, setMonth] = useState('')
  const [patient, setPatient] = useState<PersonaInfoSummary>(
    initialValue.patient
  )
  const [personaName, setPersonaName] = useState<string>()
  const [status, setStatus] = useState<MedicalRecordStatus>(
    initialValue.filter.status
  )
  const [debouncedPersonaName] = useDebounceValue(personaName, 500)

  const listResponsibleMedicalRecordQuery =
    useListResponsibleMedicalRecordQuery({
      month: month ? moment(month).month() + 1 : undefined,
      year: month ? moment(month).year() : undefined,
      personaName: debouncedPersonaName,
      status
    })

  const [selectedItemId, setSelectedItemId] = useState(
    initialValue.selectedItemId
  )
  const [reviewMode, setReviewMode] = useState<MedicalRecordReviewType>(
    initialValue.reviewMode
  )

  const {
    isOpen: isReviewViewOpen,
    onOpen: onReviewViewOpen,
    onClose: onReviewViewClose
  } = useDisclosure()

  return (
    <medicalRecordManagementContext.Provider
      value={{
        filter: {
          month,
          personaName: debouncedPersonaName,
          status
        },
        rawInputs: {
          personaName
        },
        setMonth,
        setPersonaName,
        setStatus,
        listResponsibleMedicalRecordQuery,
        isReviewViewOpen,
        onReviewViewOpen,
        onReviewViewClose,
        selectedItemId,
        setSelectedItemId,
        patient,
        setPatient,
        reviewMode,
        setReviewMode
      }}
    >
      {isReviewViewOpen ? (
        <MedicalRecordManagementRevision mode={reviewMode} />
      ) : (
        children
      )}
    </medicalRecordManagementContext.Provider>
  )
}
